import { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import { getBlueprint } from "@/services/api";
import { BaseBlueprintBuilder } from '@/components/blueprints/builder/BaseBlueprintBuilder';
import BlueprintPreview from '@/components/blueprints/shared/BlueprintPreview';
import type { BlueprintData } from '@/components/blueprints/types';
import { CARD_CATEGORIES } from "@/lib/utils/constants/cards"

interface GenerationResult {
  prompt: string;
  negative_prompt: string;
  params: {
    model: string;
    scheduler: string;
    guidanceScale: number;
    inferenceSteps: number;
    seed: number;
  };
  userId: string;
  username: string;
  imageUrl?: string;
  processingTime: number;
}

const BlueprintBuilder = () => {
  const { id } = useParams();
  const [blueprint, setBlueprint] = useState<BlueprintData | null>(null);
  const [result, setResult] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (id) {
      const loadBlueprint = async () => {
        try {
          const response = await getBlueprint(id);
          console.log('Blueprint Response:', response);
          const blueprintData = response.data.data;
          
          // Convert universe string to number for universe_id
          const universe_id = parseInt(blueprintData.universe);
          
          // Add universe name based on universe_id
          const category = Object.values(CARD_CATEGORIES).find(
            cat => cat.themeId === universe_id
          );
          
          setBlueprint({
            ...blueprintData,
            universe_id,
            universe: category?.label || 'Unknown',
            type: blueprintData.type,
            version: blueprintData.version,
            visibility: blueprintData.visibility as 'public' | 'private',
            collaborative: Boolean(blueprintData.collaborative),
            configuration: blueprintData.configuration
          });
        } catch (error) {
          console.error('Failed to load blueprint:', error);
        }
      };
      loadBlueprint();
    }
  }, [id]);

  const handleTest = (testResult: GenerationResult) => {
    setIsGenerating(true);
    setResult({
      prompt: testResult.prompt,
      imageUrl: testResult.imageUrl,
      processingTime: testResult.processingTime
    });
    
    // Add image load handling
    if (testResult.imageUrl) {
      const imageKey = testResult.imageUrl.split('/').pop();
      if (imageKey) {
        const img = new Image();
        img.onload = () => {
          setIsGenerating(false);
        };
        img.onerror = () => {
          setIsGenerating(false);
          console.error('Failed to load image');
        };
        img.src = `${import.meta.env.VITE_API_URL}/api/images/${imageKey}`;
      }
    }
  };

  const handleSave = (saveResult: GenerationResult) => {
    handleTest(saveResult);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1">
        <BaseBlueprintBuilder 
          onTest={handleTest} 
          onSave={handleSave}
          initialBlueprint={blueprint || undefined}
        />
      </div>
      <div className="w-full lg:w-[400px]">
        <BlueprintPreview 
          result={result} 
          isGenerating={isGenerating} 
        />
      </div>
    </div>
  );
};

export default BlueprintBuilder; 